import type { ColorScheme, Tuple } from "src/types/mantine-compat";

import {
  type CustomThemeData,
  type ThemeFlags,
} from "../interfaces/customTheme";

export interface ThemeColors {
  main: Tuple<string, 10>;
  selectedHeaderCell: Tuple<string, 1>;
  headerColor: Tuple<string, 10>;
  headerSky: string[];
  headerSea: string[];
  background: string[];
  cardBackground: string[];
  cardBoxShadow: string[];
  thColor: string[];
  thBackgroundColor: string[];
  thBorder: string[];
  trHoverBackgroundColor: string[];
  tdBackgroundColor: string[];
  settingsSectionBg: string[];
  settingsColor: string[];
  logbookSectionBg: string[];
  redText: string[];
  grayText: string[];
  selectItemHoverBg: string[];
  summaryStatusGold: string[];
  inputBg: string[];
  inputBackgroud: string[];
  inputBorder: string[];
  text: string[];
  grayscale: Tuple<string, 4>;
  grayscaleText: Tuple<string, 1>;
  grayscaleBorder: Tuple<string, 1>;
}

export interface ThemeReturn {
  colorScheme: ColorScheme;
  primaryColor?: string;
  flags: ThemeFlags;
  colors: ThemeColors;
}

/** Mantine 7 resolves theme colors via primaryShade (index 6); pad to 10 shades. */
const toMantineColorTuple = (
  color: string | readonly string[],
): Tuple<string, 10> => {
  const colors = Array.isArray(color) ? [...color] : [color];
  const fallback = colors[colors.length - 1] ?? colors[0] ?? "#000000";
  return Array.from({ length: 10 }, (_, i) => colors[i] ?? fallback) as Tuple<
    string,
    10
  >;
};

const makeColorArray = (color: string) => toMantineColorTuple(color);

const commonColors = (isDarkMode: boolean) => ({
  grayscale: (isDarkMode
    ? ["#666", "#4a4a4a", "#2d2d2d", "#3c3c3c", "#0f0f0f"]
    : ["#999", "#bbb", "#eee", "#f6f6f6", "#fcfcfc"]) as Tuple<string, 4>,
  grayscaleText: toMantineColorTuple(isDarkMode ? "#aaa" : "#555"),
  grayscaleBorder: toMantineColorTuple(isDarkMode ? "#555" : "#6f6f6f"),
  panelText: toMantineColorTuple(isDarkMode ? "#989fa5" : "#1e3a5f"),
  panelTextActive: toMantineColorTuple(isDarkMode ? "#ebf2ff" : "#0b1525"),
  panelHover: toMantineColorTuple(isDarkMode ? "#4e6a8f" : "#d8edff"),
});

export const generateDefaultTheme: (
  isDarkMode: boolean,
) => ThemeReturn = isDarkMode => ({
  colorScheme: isDarkMode ? "dark" : "light",
  primaryColor: "main",
  flags: {},
  colors: {
    main: toMantineColorTuple([
      "#E7F5FF",
      "#D0EBFF",
      "#A5D8FF",
      "#74C0FC",
      "#4DABF7",
      "#339AF0",
      "#228BE6",
      "#1C7ED6",
      "#1971C2",
      "#1864AB",
    ]),
    selectedHeaderCell: toMantineColorTuple("#09355f"),
    headerColor: makeColorArray("#E7F5FF"),
    headerSky: toMantineColorTuple(
      isDarkMode
        ? "linear-gradient(to right,#05131f 15%,rgba(8, 62, 112, 1) 50%,#05131f 85%)"
        : "radial-gradient(circle,rgba(24, 100, 171, 1) 0%,rgba(2, 6, 10, 1) 100%)",
    ),
    headerSea: toMantineColorTuple("#0f4c7e"),
    background: toMantineColorTuple(isDarkMode ? "#081c2e" : "#f8f9fa"),
    cardBackground: toMantineColorTuple(isDarkMode ? "#081c2e" : "#f8f9fa"),
    cardBoxShadow: toMantineColorTuple(isDarkMode ? "#56a2e98f" : "#0000003d"),
    thColor: toMantineColorTuple("#F0F8FF"),
    thBackgroundColor: toMantineColorTuple(isDarkMode ? "#0f4c7e" : "#1971c2"),
    thBorder: toMantineColorTuple(isDarkMode ? "#1864ab" : "#93b7d9"),
    trHoverBackgroundColor: toMantineColorTuple(
      isDarkMode ? "#16293a" : "#d8edff",
    ),
    tdBackgroundColor: toMantineColorTuple(
      isDarkMode ? ["#1f354a", "#27425c"] : ["#f0f8ff", "#e3f1ff"],
    ),
    settingsSectionBg: toMantineColorTuple(isDarkMode ? "#09131C" : "white"),
    settingsColor: toMantineColorTuple(isDarkMode ? "#4dabf7" : "#1864ab"),
    logbookSectionBg: toMantineColorTuple(isDarkMode ? "#09131C" : "#f3f5f7"),
    redText: toMantineColorTuple("#c92a2a"),
    grayText: toMantineColorTuple("#495057"),
    selectItemHoverBg: toMantineColorTuple(isDarkMode ? "#343A40" : "#F1F3F5"),
    summaryStatusGold: toMantineColorTuple(isDarkMode ? "#ffd700" : "#ccb11b"),
    inputBg: toMantineColorTuple(isDarkMode ? "#081c2e" : "#E9ECEF"),
    inputBackgroud: toMantineColorTuple(isDarkMode ? "#25262c" : "#ffffff"),
    inputBorder: toMantineColorTuple(isDarkMode ? "#2b323b" : "#ced4da"),
    text: makeColorArray(isDarkMode ? "#ffffff" : "#000000"),
    ...commonColors(isDarkMode),
  },
});

export const generateDSNMTheme: (
  isDarkMode: boolean,
) => ThemeReturn = isDarkMode => ({
  colorScheme: isDarkMode ? "dark" : "light",
  primaryColor: "main",
  flags: {},
  colors: {
    main: toMantineColorTuple([
      "#f8f6ed",
      "#edeae1",
      "#d9d3c3",
      "#c5bba2",
      "#b3a685",
      "#a89973",
      "#a39268",
      "#8e7e57",
      "#7f704b",
      "#6e613b",
    ]),
    selectedHeaderCell: toMantineColorTuple("#0D0E11"),
    headerColor: makeColorArray("#a89973"),
    headerSky: toMantineColorTuple("#0D0E11"),
    headerSea: toMantineColorTuple("#0D0E11"),
    background: toMantineColorTuple(isDarkMode ? "#13110B" : "#f8f9fa"),
    cardBackground: toMantineColorTuple(isDarkMode ? "#0D0E11" : "#f8f9fa"),
    cardBoxShadow: toMantineColorTuple(isDarkMode ? "#6e613b" : "#0000003d"),
    thColor: toMantineColorTuple(isDarkMode ? "#a89973" : "#fff"),
    thBackgroundColor: toMantineColorTuple(isDarkMode ? "#1e1c14" : "#7f704b"),
    thBorder: toMantineColorTuple(isDarkMode ? "#a89973" : "#f8f6ed"),
    trHoverBackgroundColor: toMantineColorTuple(
      isDarkMode ? "#1e1c14" : "#d9d3c3",
    ),
    tdBackgroundColor: toMantineColorTuple(
      isDarkMode ? ["#0D0E11", "#16181c"] : ["#f8f6ed", "#f7f2d7"],
    ),
    settingsSectionBg: toMantineColorTuple(isDarkMode ? "#0D0E11" : "white"),
    settingsColor: toMantineColorTuple("#6e613b"),
    logbookSectionBg: toMantineColorTuple(isDarkMode ? "#080807" : "#f3f5f7"),
    redText: toMantineColorTuple("#c92a2a"),
    grayText: toMantineColorTuple("#495057"),
    selectItemHoverBg: toMantineColorTuple(isDarkMode ? "#343A40" : "#F1F3F5"),
    summaryStatusGold: toMantineColorTuple(isDarkMode ? "#ffd700" : "#ccb11b"),
    inputBg: toMantineColorTuple(isDarkMode ? "#081c2e" : "#E9ECEF"),
    inputBackgroud: toMantineColorTuple(isDarkMode ? "#25262c" : "#ffffff"),
    inputBorder: toMantineColorTuple(isDarkMode ? "#2b323b" : "#ced4da"),
    text: makeColorArray(isDarkMode ? "#ffffff" : "#000000"),
    ...commonColors(isDarkMode),
  },
});

export const generateCustomTheme = (
  isDarkMode: boolean,
  customTheme: CustomThemeData,
): ThemeReturn => {
  const mode = isDarkMode ? "dark" : "light";
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- omitted from the rest props on purpose
  const { dark: _dark, light: _light, ...flags } = customTheme;
  const theme = customTheme[mode];

  if (!theme) {
    throw new Error(`Custom theme data not found for mode "${mode}"`);
  }

  return {
    colorScheme: mode,
    primaryColor: "main",
    flags,
    colors: {
      main: makeColorArray(theme.buttonColor),
      selectedHeaderCell: toMantineColorTuple(theme.selectedHeaderCellColor),
      headerColor: makeColorArray(theme.headerTextColor),
      headerSky: toMantineColorTuple(
        !flags.hideHeader && theme.headerSkyGradient
          ? theme.headerSkyGradient
          : "none",
      ),
      headerSea: toMantineColorTuple(
        !flags.hideHeader && theme.headerSeaColor
          ? theme.headerSeaColor
          : "none",
      ),
      background: toMantineColorTuple(theme.backgroundColor),
      cardBackground: toMantineColorTuple(theme.cardBackgroundColor),
      cardBoxShadow: toMantineColorTuple(theme.cardBoxShadowColor),
      thColor: toMantineColorTuple(theme.tableHeaderTextColor),
      thBackgroundColor: toMantineColorTuple(theme.tableHeaderBackground),
      thBorder: toMantineColorTuple(theme.tableHeaderBorderColor),
      trHoverBackgroundColor: toMantineColorTuple(
        theme.tableRowHoverBackground,
      ),
      tdBackgroundColor: toMantineColorTuple(
        Array.isArray(theme.tableCellBackgrounds)
          ? theme.tableCellBackgrounds
          : [theme.tableCellBackgrounds, theme.tableCellBackgrounds],
      ),
      settingsSectionBg: toMantineColorTuple(theme.settingsSectionBackground),
      settingsColor: toMantineColorTuple(theme.settingsTextColor),
      logbookSectionBg: toMantineColorTuple(theme.logbookSectionBackground),
      redText: toMantineColorTuple(theme.redTextColor),
      grayText: toMantineColorTuple(theme.grayTextColor),
      selectItemHoverBg: toMantineColorTuple(theme.selectItemHoverBackground),
      summaryStatusGold: toMantineColorTuple(theme.summaryStatusGoldColor),
      inputBg: toMantineColorTuple(theme.inputBackgroundColor),
      inputBackgroud: toMantineColorTuple(theme.inputBackgroundColorAlt),
      inputBorder: toMantineColorTuple(theme.inputBorderColor),
      text: makeColorArray(theme.textColor),
      ...commonColors(isDarkMode),
    },
  };
};
